// Lógica PURA do "aplicar ao grupo econômico" da tela Automações (TXM-655).
//
// Grupo econômico = empresas de RAÍZES de CNPJ diferentes ligadas por um vínculo comercial manual
// (id_grupo_economico). Não confundir com matriz/filiais, que são a MESMA raiz (8 primeiros dígitos)
// e já têm tratamento próprio no SPED Fiscal (refinamento por estabelecimento, TXM-352).
//
// Fica fora do componente para ser testável sem montar o modal.

const soDigitos = (v) => (v || "").replace(/\D/g, "");

/** Raiz do CNPJ (8 primeiros dígitos): é o que identifica a empresa; o resto é o estabelecimento. */
export const raizCnpj = (cnpj) => soDigitos(cnpj).slice(0, 8);

/** Ordem do estabelecimento (dígitos 9-12). "0001" = matriz por convenção da Receita. */
const ordemCnpj = (cnpj) => soDigitos(cnpj).slice(8, 12);

/**
 * Normaliza a lista crua devolvida pela api-empresas para as empresas do grupo.
 * - só ativas e com CNPJ completo (14 dígitos);
 * - sem duplicadas (por CNPJ);
 * - `empresaBase` (a que o usuário escolheu) SEMPRE entra: ela pertence ao grupo mesmo que a
 *   listagem não a devolva por algum motivo;
 * - matriz primeiro, depois por razão social (mesma ordem da query do backend).
 */
export function normalizarEmpresasDoGrupo(lista, empresaBase) {
  const porCnpj = new Map();

  for (const e of Array.isArray(lista) ? lista : []) {
    const cnpj = soDigitos(e?.cnpj);
    const ativo = String(e?.status ?? "ATIVO").toUpperCase() !== "INATIVO";
    if (!ativo || cnpj.length !== 14 || porCnpj.has(cnpj)) continue;
    porCnpj.set(cnpj, {
      cnpj,
      razaoSocial: e.razao_social || e.razaoSocial || e.nome_empresarial || "",
      isMatriz: !!(e.is_matriz ?? e.isMatriz),
    });
  }

  const baseCnpj = soDigitos(empresaBase?.cnpj);
  if (baseCnpj.length === 14 && !porCnpj.has(baseCnpj)) {
    porCnpj.set(baseCnpj, {
      cnpj: baseCnpj,
      razaoSocial: empresaBase.razaoSocial || "",
      isMatriz: !!empresaBase.isMatriz,
    });
  }

  return [...porCnpj.values()].sort(
    (a, b) =>
      (b.isMatriz - a.isMatriz) ||
      a.razaoSocial.localeCompare(b.razaoSocial, "pt-BR") ||
      a.cnpj.localeCompare(b.cnpj)
  );
}

/**
 * Decide QUAIS empresas viram solicitação (uma por empresa).
 *
 * SPED Fiscal é a exceção: a pesquisa dele já traz TODOS os estabelecimentos da raiz num pedido só
 * (todos_estabelecimentos=true, o padrão). Se o grupo listar a matriz E uma filial da mesma raiz e
 * disparássemos as duas, o segundo pedido repetiria a coleta na Receita — e a central não detecta
 * a repetição, porque o CNPJ é outro. Então, no Fiscal, colapsamos por raiz: uma solicitação por
 * raiz, preferindo a matriz. Nos demais SPED, uma por empresa selecionada.
 *
 * @returns {{cnpj: string, razaoSocial: string}[]}
 */
export function montarAlvosDoGrupo(empresas, spedTipo) {
  const lista = Array.isArray(empresas) ? empresas : [];

  if (spedTipo !== "fiscal") {
    const vistos = new Set();
    return lista
      .filter((e) => (vistos.has(e.cnpj) ? false : vistos.add(e.cnpj)))
      .map(({ cnpj, razaoSocial }) => ({ cnpj, razaoSocial }));
  }

  // melhor representante de cada raiz: matriz > ordem 0001 > primeiro que apareceu
  const pontos = (e) => (e.isMatriz ? 2 : 0) + (ordemCnpj(e.cnpj) === "0001" ? 1 : 0);
  const porRaiz = new Map();
  for (const e of lista) {
    const raiz = raizCnpj(e.cnpj);
    const atual = porRaiz.get(raiz);
    if (!atual || pontos(e) > pontos(atual)) porRaiz.set(raiz, e);
  }
  return [...porRaiz.values()].map(({ cnpj, razaoSocial }) => ({ cnpj, razaoSocial }));
}
